// Typed wrappers around the agent's `/api/auth/*` endpoints + the
// in-memory auth stores (authState + authUser).
//
// PR 2 of `auth-via-telegram-otp`. The agent (PR 1) accepts:
//
//   POST /api/auth/request   { username }
//     → 200 { ttl_secs }
//       403 (opaque — non-allowlisted user)
//       423 / 429 (rate-limit / lockout)
//
//   POST /api/auth/verify    { username, code }
//     → 200 { user, csrf_token }   + Set-Cookie: dashboard_session
//       401 (wrong / expired)
//       423 (lockout)
//
//   POST /api/auth/logout
//     → 200   + Set-Cookie clears session
//
//   GET  /api/auth/me
//     → 200 { user, csrf_token, expires_at }
//       401 (no session / expired)
//
// Mutating endpoints require X-CSRF-Token from the session — call
// `csrfHeader()` to read it on demand. `applyAuth({state, user})`
// stores the token so mutates can pick it up without an extra fetch.

import { createStore, getValue, type StoreWithDerived } from "./store";

// --- types --------------------------------------------------------------

/**
 * The discriminated `kind` mirrors the wire error codes the agent
 * emits so the UI can render the right banner without re-deriving from
 * a numeric status. Adding a new kind is a typed compile error at
 * every consumer thanks to the switch in `kindFromStatus`.
 */
export type AuthErrorKind =
  | "forbidden"        // 403 — opaque "unknown user" or generic deny
  | "wrong_code"       // 401 — code mismatch
  | "expired"          // 401 — code was consumed or aged out
  | "rate_limited"     // 429 — back off and retry
  | "locked"           // 423 — verify-failure lockout (15 min)
  | "network"          // fetch threw (offline / CORS / DNS)
  | "server_error"     // 5xx — opaque "try again later"
  | "unknown";         // anything else we haven't classified

export interface SessionInfo {
  user: string;
  csrf: string;
  expires_at: number;
}

export interface AuthUserSnapshot {
  user: string;
  csrf: string;
}

export type AuthStateValue =
  | "unknown"          // initial; App.svelte shows a brief loader
  | "unauthenticated"
  | "authenticated";

export class AuthError extends Error {
  readonly kind: AuthErrorKind;
  readonly status: number;
  readonly retryAfter: number | undefined;
  constructor(
    kind: AuthErrorKind,
    status: number,
    message: string,
    retryAfter?: number,
  ) {
    super(message);
    this.name = "AuthError";
    this.kind = kind;
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

// --- module-singleton stores -------------------------------------------

const authStateStore: StoreWithDerived<AuthStateValue> =
  createStore<AuthStateValue>("unknown");

const authUserStore: StoreWithDerived<AuthUserSnapshot | null> =
  createStore<AuthUserSnapshot | null>(null);

export const authState: Readonly<StoreWithDerived<AuthStateValue>> =
  authStateStore;

export const authUser: Readonly<StoreWithDerived<AuthUserSnapshot | null>> =
  authUserStore;

export { getValue };

/**
 * Combined setter — flipping `state` to "unauthenticated" also clears
 * the user payload, and vice-versa. Both transitions land atomically
 * from the perspective of any subscriber attached after the call.
 */
export function applyAuth(input: {
  state: AuthStateValue;
  user?: AuthUserSnapshot | null;
}): void {
  authStateStore.set(input.state);
  if (input.user === undefined) {
    // No user info carried in the change — only update the user
    // store when the caller asked us to. Setting to `null` here would
    // clobber an existing value when the caller only meant to flip the
    // state.
    return;
  }
  authUserStore.set(input.user);
}

// --- wire helpers -------------------------------------------------------

/** True when the URL is one of the public auth endpoints we should NOT
 *  treat as a session-expiry signal when it 401s (it's expected). */
function isAuthEndpoint(url: string): boolean {
  return url.startsWith("/api/auth/");
}

function retryAfterFromHeaders(res: Response): number | undefined {
  const v = res.headers.get("retry-after");
  if (v === null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function statusKind(status: number): AuthErrorKind {
  switch (status) {
    case 401: return "wrong_code";
    case 403: return "forbidden";
    case 423: return "locked";
    case 429: return "rate_limited";
    default:
      return status >= 500 ? "server_error" : "unknown";
  }
}

/**
 * Internal typed fetch that maps non-2xx into an `AuthError` with the
 * right `kind`. The `getSession()` path uses `safeFetch` instead —
 * it needs the 401 to mean "no session", not an error.
 */
async function jsonFetch<T>(
  path: string,
  init: RequestInit,
  expectedStatus: number[] = [200, 204],
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init.headers ?? {}),
      },
    });
  } catch (err) {
    throw new AuthError(
      "network",
      0,
      err instanceof Error ? err.message : String(err),
    );
  }
  if (expectedStatus.includes(res.status)) {
    if (res.status === 204) return undefined as unknown as T;
    const text = await res.text();
    if (text.length === 0) return undefined as unknown as T;
    return JSON.parse(text) as T;
  }
  const body = await res.text().catch(() => "");
  const kind = statusKind(res.status);
  throw new AuthError(
    kind,
    res.status,
    body || res.statusText,
    retryAfterFromHeaders(res),
  );
}

// --- public API ---------------------------------------------------------

/**
 * Returns the active session — `null` if there is none (HTTP 401).
 * Network errors bubble up via `AuthError(kind=network)` so the caller
 * can choose whether to retry or surface an error.
 */
export async function getSession(): Promise<SessionInfo | null> {
  let res: Response;
  try {
    res = await fetch("/api/auth/me", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch (err) {
    throw new AuthError(
      "network",
      0,
      err instanceof Error ? err.message : String(err),
    );
  }
  if (res.status === 401) return null;
  if (!res.ok) {
    throw new AuthError(
      statusKind(res.status),
      res.status,
      await res.text().catch(() => res.statusText),
    );
  }
  const body = (await res.json()) as {
    user: string;
    csrf_token: string;
    expires_at: number;
  };
  return {
    user: body.user,
    csrf: body.csrf_token,
    expires_at: body.expires_at,
  };
}

/** Request an OTP — fires `POST /api/auth/request`. */
export async function requestOtp(username: string): Promise<void> {
  await jsonFetch<{ ttl_secs: number }>(
    "/api/auth/request",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    },
    [200],
  );
}

/** Verify the OTP — fires `POST /api/auth/verify`. Stores the resulting
 *  CSRF token + user on success so subsequent mutates can read them. */
export async function verifyOtp(username: string, code: string): Promise<void> {
  const body = await jsonFetch<{ user: string; csrf_token: string }>(
    "/api/auth/verify",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, code }),
    },
    [200],
  );
  applyAuth({
    state: "authenticated",
    user: { user: body.user, csrf: body.csrf_token },
  });
}

/** Drop the session — clears auth state on success. */
export async function logout(): Promise<void> {
  await jsonFetch<unknown>(
    "/api/auth/logout",
    { method: "POST" },
    [200, 204],
  );
  applyAuth({ state: "unauthenticated", user: null });
}

/**
 * Header injection for mutating endpoints. Returns an empty object
 * when unauthenticated so callers can spread unconditionally.
 */
export function csrfHeader(): Record<string, string> {
  const u = getValue(authUser);
  if (u === null) return {};
  return { "X-CSRF-Token": u.csrf };
}

// --- global 401 interceptor --------------------------------------------

/**
 * Wraps `window.fetch` so any `/api/*` (excluding the auth endpoints
 * themselves) returning 401 while the user is currently authenticated
 * flips the auth state to `unauthenticated`. App.svelte observes the
 * store flip and re-mounts `<PageLogin />`.
 *
 * Installed exactly once per module load. Idempotent across HMR by
 * stashing a flag on `window`.
 */
export function installAuthInterceptor(): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as { __authInterceptorInstalled?: boolean };
  if (w.__authInterceptorInstalled) return;
  w.__authInterceptorInstalled = true;

  const orig = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.pathname + input.search
          : input.url;
    const res = await orig(input, init);
    if (
      res.status === 401 &&
      url.startsWith("/api/") &&
      !isAuthEndpoint(url) &&
      getValue(authStateStore) === "authenticated"
    ) {
      applyAuth({ state: "unauthenticated", user: null });
    }
    return res;
  };
}

// Public utility — exported so tests can re-trigger the interceptor
// after `vi.resetModules()`. Keeping it `unknown` so we don't pollute
// the public types.
export const __test__isAuthEndpoint: (url: string) => boolean = isAuthEndpoint;
